import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Megaphone, Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const EMPTY = { title: "", description: "", start: "", end: "", link: "" };
const local = (iso) => { const d = new Date(iso); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
const fmt = (s) => new Date(s).toLocaleDateString(undefined, { dateStyle: "medium" });
const statusOf = (p, now) => (p.end <= now ? "Ended" : p.start <= now ? "Running" : "Upcoming");

const PromoForm = ({ initial, onDone }) => {
  const [f, setF] = useState(initial ? { ...initial, start: local(initial.start), end: local(initial.end), link: initial.link || "" } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (f.link.trim() && !/^https:\/\/\S+$/.test(f.link.trim())) return toast.error("Link must start with https://");
    setBusy(true);
    const body = { title: f.title, description: f.description, start: new Date(f.start).toISOString(), end: new Date(f.end).toISOString(), link: f.link.trim() || null };
    try { await (initial ? api.put(`/admin/promotions/${initial.id}`, body) : api.post("/admin/promotions", body)); toast.success(initial ? "Promotion updated" : "Promotion added"); onDone(); if (!initial) setF(EMPTY); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="promotion-form">
      <label className="text-xs text-[#A8A090]">Title<Input value={f.title} onChange={set("title")} placeholder="National Novel Writing Month" className="mt-1" data-testid="promotion-title-input" /></label>
      <label className="text-xs text-[#A8A090]">Link (optional)<Input value={f.link} onChange={set("link")} placeholder="https://…" className="mt-1" data-testid="promotion-link-input" /></label>
      <label className="text-xs text-[#A8A090] sm:col-span-2">Short description<Textarea value={f.description} onChange={set("description")} rows={2} maxLength={400} className="mt-1" data-testid="promotion-description-input" /></label>
      <label className="text-xs text-[#A8A090]">Starts<Input type="datetime-local" value={f.start} onChange={set("start")} className="mt-1 [color-scheme:dark]" data-testid="promotion-start-input" /></label>
      <label className="text-xs text-[#A8A090]">Ends<Input type="datetime-local" value={f.end} onChange={set("end")} className="mt-1 [color-scheme:dark]" data-testid="promotion-end-input" /></label>
      <div className="flex gap-2 sm:col-span-2">
        <Button onClick={save} disabled={busy || !f.title.trim() || !f.start || !f.end} data-testid="promotion-save-button">{initial ? "Save changes" : "Add promotion"}</Button>
        {initial && <Button variant="ghost" onClick={onDone} data-testid="promotion-cancel-button">Cancel</Button>}
      </div>
    </div>
  );
};

const Row = ({ p, now, onEdit, onDelete }) => (
  <li className="flex flex-wrap items-start justify-between gap-3 p-4" data-testid={`promotion-row-${p.id}`}>
    <div className="min-w-0">
      <div className="text-[#F3EBDD]">{p.title} <span className="ml-2 rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-[#FBBF24]" data-testid={`promotion-status-${p.id}`}>{statusOf(p, now)}</span></div>
      <div className="mt-1 text-xs text-[#A8A090]">{fmt(p.start)} → {fmt(p.end)}{p.link && <> · <a href={p.link} target="_blank" rel="noreferrer" className="text-[#F59E0B] hover:underline">link</a></>}</div>
      {p.description && <p className="mt-1 text-sm text-[#D6CCBC]">{p.description}</p>}
    </div>
    <div className="flex gap-1">
      <Button size="icon" variant="ghost" onClick={onEdit} aria-label="Edit" data-testid={`promotion-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button size="icon" variant="ghost" onClick={onDelete} aria-label="Delete" data-testid={`promotion-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  </li>
);

export const PromotionsPanel = () => {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-promotions"], queryFn: () => api.get("/admin/promotions").then((r) => r.data) });
  const [editing, setEditing] = useState(null);
  const refresh = () => { setEditing(null); qc.invalidateQueries({ queryKey: ["admin-promotions"] }); };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/admin/promotions/${p.id}`); toast.success("Promotion deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const now = new Date().toISOString();
  return (
    <section className="space-y-6 rounded-md border border-[#2D3442] bg-[#0F131A] p-6" data-testid="admin-promotions">
      <div>
        <div className="flex items-center gap-2"><Megaphone className="h-4 w-4 text-[#FBBF24]" /><div className="lf-label">Promotions</div></div>
        <p className="mt-2 text-xs text-[#A8A090]">Promotions that are running, or start within 30 days, appear in invite emails and on the invite sign-up page. They disappear after they end.</p>
      </div>
      {editing ? <PromoForm key={editing.id} initial={editing} onDone={refresh} /> : <PromoForm onDone={refresh} />}
      {data.length > 0 && <ul className="divide-y divide-[#2D3442] rounded-md border border-[#2D3442]" data-testid="promotion-list">
        {data.map((p) => <Row key={p.id} p={p} now={now} onEdit={() => setEditing(p)} onDelete={() => remove(p)} />)}
      </ul>}
    </section>
  );
};
